import { type ReactNode, createContext, useEffect, useState } from 'react';

export interface MicrophoneContextProps {
  audioContext: AudioContext | null;
  audioSource: MediaStreamAudioSourceNode | null;
  mediaStream: MediaStream | null;
}

export const MicrophoneContext = createContext<MicrophoneContextProps>({
  audioContext: null,
  audioSource: null,
  mediaStream: null,
});

interface MicrophoneProviderProps {
  children: ReactNode;
}

const MicrophoneProvider = ({ children }: MicrophoneProviderProps) => {
  const [audioSource, setAudioSource] =
    useState<MediaStreamAudioSourceNode | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    (async () => {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
    })();
  }, []);

  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  useEffect(() => {
    const context = new AudioContext();
    const bodyClickHandler = () => {
      if (context.state === 'closed') {
        return;
      }
      context.resume();
    };

    document.body.addEventListener('click', bodyClickHandler, {
      once: true,
    });

    setAudioContext(context);

    return () => {
      document.body.removeEventListener('click', bodyClickHandler);
      context.close();
    };
  }, []);

  useEffect(() => {
    if (!mediaStream || !audioContext) {
      return;
    }
    const source = audioContext.createMediaStreamSource(mediaStream);
    setAudioSource(source);

    return () => {
      const tracks = mediaStream.getTracks();

      tracks.forEach((track) => {
        track.stop();
      });
    };
  }, [mediaStream, audioContext]);

  return (
    <MicrophoneContext.Provider
      value={{ audioContext, audioSource, mediaStream }}
    >
      {children}
    </MicrophoneContext.Provider>
  );
};

export default MicrophoneProvider;
